import React, { useRef, useState } from 'react';
import { Box, TextField } from '@mui/material';
import PlacesAutocompleteMui from '../../placesAutocomplete/PlacesAutocompleteMui';
import { PlacePrediction } from '../../placesAutocomplete/interfaces';
import { getQueryParamsFromPacItem } from '../../../util/AddressUtils';
import useClickOutsideRef from '../../../hooks/useClickOutsideRef';

// Restrict suggestions to US cities (localities) — this field requests space in
// a city/area, not at a specific street address.
const CITY_PRIMARY_TYPES = ['locality'];

interface CityStateAutocompleteProps {
  /** Current text shown in the field (e.g. "Los Angeles, CA"). */
  value: string;
  /** Raw text change (typing). The parent should clear the resolved city/state here. */
  onChange: (text: string) => void;
  /** Fired when the user picks a city — provides the selected Places prediction
   *  (place_id + structured text) so the consumer can both label the field and
   *  search/navigate to that city. */
  onCitySelected: (pacItem: PlacePrediction) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
}

/**
 * Editable "City, State" field backed by Google Places autocomplete (US cities),
 * mirroring the landing-page search. City/state are parsed from the selected
 * prediction's text — no geocoding call is made.
 */
const CityStateAutocomplete = ({
  value,
  onChange,
  onCitySelected,
  label = 'City, State',
  error,
  helperText,
}: CityStateAutocompleteProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPac, setShowPac] = useState(false);
  const [touched, setTouched] = useState(false);
  const [selectedPacItem, setSelectedPacItem] = useState<PlacePrediction | undefined>();

  useClickOutsideRef(containerRef, () => setShowPac(false));

  const handleClickPac = (pacItem: PlacePrediction) => {
    const params = getQueryParamsFromPacItem(pacItem);
    if (params?.city && params?.state) {
      // Pass the full prediction up so the consumer can label the field AND
      // search/navigate to the picked city. Don't call onChange here (that's the
      // typing path, which clears the resolved city/state).
      onCitySelected(pacItem);
    }
    setSelectedPacItem(undefined);
    setShowPac(false);
  };

  return (
    <Box ref={containerRef}>
      <TextField
        fullWidth
        inputRef={inputRef}
        value={value}
        label={label}
        name="cityState"
        autoComplete="off"
        error={touched && !!error}
        helperText={touched ? helperText : ''}
        onChange={(event) => {
          onChange(event.target.value);
          setShowPac(true);
        }}
        onFocus={() => setShowPac(true)}
        onBlur={() => setTouched(true)}
      />
      {showPac && !!value && (
        <PlacesAutocompleteMui
          inputRef={inputRef}
          inputValue={value}
          containerRef={containerRef}
          onClickPac={handleClickPac}
          handleSelectPac={setSelectedPacItem}
          selectedPacItem={selectedPacItem}
          includedPrimaryTypes={CITY_PRIMARY_TYPES}
        />
      )}
    </Box>
  );
};

export default CityStateAutocomplete;
